/* ARIORA: scroll motion, progress bar, sticky order bar. Content stays visible if this file fails. */
(() => {
  const doc = document;
  const root = doc.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Elements that already animate themselves are left alone.
  const SKIP = '.ai-marquee, [class*="ai-marquee-"], [class*="ai-steps-"], [class*="ai-compare-"], [class*="ai-reviews"], .announcement-bar, header, .header-wrapper, .product__media-wrapper, .ar-buybar';

  function splitWords(el) {
    if (el.dataset.arSplit) return;
    el.dataset.arSplit = '1';
    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let n = 0;
    nodes.forEach((t) => {
      if (!t.nodeValue.trim()) return;
      const frag = doc.createDocumentFragment();
      t.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(part)); return; }
        const outer = doc.createElement('span');
        outer.className = 'ar-w';
        const inner = doc.createElement('span');
        inner.textContent = part;
        inner.style.setProperty('--wd', Math.min(n * 55, 700) + 'ms');
        outer.appendChild(inner);
        frag.appendChild(outer);
        n += 1;
      });
      t.parentNode.replaceChild(frag, t);
    });
    el.classList.add('ar-split');
  }

  function setupReveal() {
    const sections = doc.querySelectorAll('main .shopify-section, .footer');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('ar-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    sections.forEach((section) => {
      const found = Array.from(section.querySelectorAll(
        'h1, h2, h3, .banner__heading, .rte > *, .image-with-text__text, .rich-text__text, .button, .collapsible-content__wrapper details, .image-with-text__media, .multirow .image-with-text__media, .icon-with-text, .newsletter-form, .footer-block'
      )).filter((el) => !el.closest(SKIP));
      // keep only outermost matches so nothing animates twice
      const targets = found.filter((el) => !found.some((o) => o !== el && o.contains(el)));
      targets.forEach((el, i) => {
        el.style.setProperty('--d', Math.min(i * 85, 680) + 'ms');
        if (el.matches('.image-with-text__media, .multirow .image-with-text__media')) {
          el.classList.add('ar-img');
        } else if (el.matches('h1, h2, .banner__heading')) {
          splitWords(el);
        } else {
          el.classList.add('ar-r');
        }
        io.observe(el);
        pending.push(el);
      });
    });

    // Backup for browsers that delay IntersectionObserver: reveal whatever is on screen.
    const sweep = () => {
      const limit = window.innerHeight * 0.95;
      for (let i = pending.length - 1; i >= 0; i -= 1) {
        const el = pending[i];
        if (el.classList.contains('ar-in')) { pending.splice(i, 1); continue; }
        const r = el.getBoundingClientRect();
        if (r.top < limit && r.bottom > 0) { el.classList.add('ar-in'); pending.splice(i, 1); }
      }
    };
    let timer = 0;
    window.addEventListener('scroll', () => {
      if (!timer) timer = setTimeout(() => { timer = 0; sweep(); }, 120);
    }, { passive: true });
    setTimeout(sweep, 400);
    setTimeout(sweep, 1500);
  }
  const pending = [];

  function setupScroll() {
    const bar = doc.createElement('div');
    bar.className = 'ar-progress';
    bar.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(bar);
    const hero = doc.querySelector('.banner__media');
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const max = root.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (max > 0 ? Math.min(y / max, 1) : 0) + ')';
      if (hero && !reduce && y < window.innerHeight * 1.3) {
        hero.style.transform = 'translate3d(0,' + (y * 0.28).toFixed(1) + 'px,0)';
      }
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  function setupBuyBar() {
    const submit = doc.querySelector('main .product-form__submit');
    if (!submit) return;
    const titleEl = doc.querySelector('.product__title h1, .product__title');
    const name = (titleEl && titleEl.textContent.trim()) || 'מעמד ARIORA';
    const bar = doc.createElement('div');
    bar.className = 'ar-buybar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'הזמנה מהירה');
    bar.innerHTML = '<span class="ar-buybar__name"></span><button type="button" class="ar-buybar__go">להזמנה ←</button>';
    bar.querySelector('.ar-buybar__name').textContent = name;
    doc.body.appendChild(bar);
    bar.querySelector('.ar-buybar__go').addEventListener('click', () => {
      submit.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      setTimeout(() => submit.focus({ preventScroll: true }), reduce ? 0 : 600);
    });
    new IntersectionObserver(([e]) => {
      const past = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-on', past);
    }).observe(submit);
  }

  // Page images drift slowly inside their frames while the page scrolls.
  function setupDrift() {
    if (reduce) return;
    const frames = Array.from(doc.querySelectorAll(
      'main .image-with-text__media, main .multirow .image-with-text__media, main .shopify-section:not(:first-child) .banner__media'
    )).filter((f) => f.querySelector('img') && !f.closest('.product__media-wrapper'));
    if (!frames.length) return;
    const live = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? live.add(e.target) : live.delete(e.target)));
      tick();
    }, { rootMargin: '10% 0px' });
    frames.forEach((f) => { f.classList.add('ar-drift'); io.observe(f); });
    let queued = false;
    function tick() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        const vh = window.innerHeight;
        live.forEach((f) => {
          const r = f.getBoundingClientRect();
          const p = ((r.top + r.height / 2) - vh / 2) / vh; // -1..1 across the screen
          f.querySelector('img').style.transform = 'translate3d(0,' + (p * -6).toFixed(2) + '%,0) scale(1.12)';
        });
      });
    }
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
  }

  // Interactive stand: gold stars orbit it, the pointer tilts it, a tap plays the NFC moment.
  function setupOrbit(section) {
    const stage = section.querySelector('.ar-orbit__stage');
    const field = section.querySelector('.ar-orbit__stars');
    const stand = section.querySelector('.ar-orbit__stand');
    const toast = section.querySelector('.ar-orbit__toast');
    if (!stage || !field || !stand) return;

    const STAR = '<svg viewBox="0 0 24 24"><path d="M12 1.8l3.1 6.4 7 1-5.1 4.9 1.2 7-6.2-3.3-6.2 3.3 1.2-7L1.9 9.2l7-1z"/></svg>';
    const N = window.innerWidth < 750 ? 11 : 16;
    const stars = [];
    for (let i = 0; i < N; i += 1) {
      const el = doc.createElement('span');
      el.className = 'ar-star';
      const size = 13 + Math.round((i * 7) % 22);
      el.style.setProperty('--s', size + 'px');
      el.innerHTML = STAR;
      field.appendChild(el);
      stars.push({
        el,
        a: (i / N) * Math.PI * 2,
        ring: 0.62 + ((i * 37) % 30) / 100, // orbit radius as share of half-stage
        tiltY: 0.32 + ((i * 13) % 20) / 100,
        speed: 0.18 + ((i * 29) % 25) / 100,
        x: 0, y: 0, vx: 0, vy: 0, // spring offset from the orbit (pointer push)
        tx: null, ty: null, // tap target
      });
    }

    let w = 0, h = 0;
    const measure = () => { const r = stage.getBoundingClientRect(); w = r.width; h = r.height; };
    measure();
    window.addEventListener('resize', measure);

    let px = 0, py = 0, hot = false, tapUntil = 0, boost = 1, tiltX = 0, tiltY = 0;
    stage.addEventListener('pointermove', (e) => {
      const r = stage.getBoundingClientRect();
      px = (e.clientX - r.left) / r.width - 0.5;
      py = (e.clientY - r.top) / r.height - 0.5;
      hot = true;
      section.classList.add('is-hot');
    });
    stage.addEventListener('pointerleave', () => { hot = false; px = 0; py = 0; section.classList.remove('is-hot'); });

    const play = () => {
      tapUntil = performance.now() + 2600;
      section.classList.remove('is-tap');
      void section.offsetWidth;
      section.classList.add('is-tap');
      if (toast) toast.textContent = section.dataset.toast || '';
      // five stars line up above the stand, the rest scatter outward
      stars.forEach((s, i) => {
        if (i < 5) { s.tx = (i - 2) * Math.min(w * 0.13, 46); s.ty = -h * 0.42; }
        else { s.tx = null; s.vx += Math.cos(s.a) * 18; s.vy += Math.sin(s.a) * 18; }
      });
      clearTimeout(play.t);
      play.t = setTimeout(() => {
        section.classList.remove('is-tap');
        stars.forEach((s) => { s.tx = null; s.ty = null; });
      }, 2600);
    };
    stand.addEventListener('click', play);

    let running = false, last = 0, raf = 0;
    const frame = (t) => {
      const dt = Math.min((t - last) / 1000, 0.05);
      last = t;
      boost += ((hot ? 2.6 : 1) - boost) * 0.06;
      tiltX += ((hot ? -py * 16 : Math.sin(t / 2400) * 3) - tiltX) * 0.08;
      tiltY += ((hot ? px * 22 : Math.cos(t / 3000) * 5) - tiltY) * 0.08;
      const bob = Math.sin(t / 1100) * 6;
      stand.style.transform = 'translate3d(0,' + bob.toFixed(1) + 'px,0) rotateX(' + tiltX.toFixed(2) + 'deg) rotateY(' + tiltY.toFixed(2) + 'deg)';

      const R = Math.min(w, h) / 2;
      const mx = px * w, my = py * h;
      const tapping = t < tapUntil;
      stars.forEach((s) => {
        s.a += s.speed * boost * dt * (tapping ? 0.3 : 1);
        const ox = Math.cos(s.a) * R * s.ring;
        const oy = Math.sin(s.a) * R * s.ring * s.tiltY + tiltX * 2;
        const depth = Math.sin(s.a); // >0 in front of the stand
        let gx = ox, gy = oy;
        if (s.tx !== null) { gx = s.tx; gy = s.ty; }
        // pointer pushes nearby stars away
        if (hot) {
          const dx = ox + s.x - mx, dy = oy + s.y - my, dist = Math.hypot(dx, dy) || 1;
          if (dist < R * 0.45) { const f = (1 - dist / (R * 0.45)) * 900 * dt; s.vx += (dx / dist) * f; s.vy += (dy / dist) * f; }
        }
        // spring back toward the orbit (or the tap target)
        const k = s.tx !== null ? 0.12 : 0.05;
        s.vx += ((gx - ox) - s.x) * k; s.vy += ((gy - oy) - s.y) * k;
        s.vx *= 0.82; s.vy *= 0.82;
        s.x += s.vx; s.y += s.vy;
        const lined = s.tx !== null;
        const sc = lined ? 1.9 : 0.75 + (depth + 1) * 0.3;
        s.el.style.transform = 'translate3d(' + (ox + s.x).toFixed(1) + 'px,' + (oy + s.y).toFixed(1) + 'px,0) scale(' + sc.toFixed(2) + ') rotate(' + (s.a * 40).toFixed(0) + 'deg)';
        s.el.style.opacity = lined ? '1' : (0.35 + (depth + 1) * 0.32).toFixed(2);
        s.el.style.zIndex = lined || depth > 0 ? '8' : '1';
      });
      raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!running) { running = true; last = performance.now(); raf = requestAnimationFrame(frame); } };
    const stop = () => { running = false; cancelAnimationFrame(raf); };

    if (reduce) {
      // still picture: stars resting on their orbit, tap still shows the rings and message
      stars.forEach((s) => {
        const R = Math.min(w, h) / 2;
        s.el.style.transform = 'translate3d(' + (Math.cos(s.a) * R * s.ring).toFixed(1) + 'px,' + (Math.sin(s.a) * R * s.ring * s.tiltY).toFixed(1) + 'px,0)';
        s.el.style.opacity = '.8';
      });
      return;
    }
    let inView = false;
    new IntersectionObserver(([e]) => { inView = e.isIntersecting; if (inView) start(); else stop(); }).observe(section);
    doc.addEventListener('visibilitychange', () => { if (doc.hidden) stop(); else if (inView) start(); });
  }

  function init() {
    setupScroll();
    setupBuyBar();
    setupDrift();
    doc.querySelectorAll('[data-ar-orbit]').forEach(setupOrbit);
    if (reduce || root.classList.contains('shopify-design-mode') || !('IntersectionObserver' in window)) return;
    root.classList.add('ar-motion');
    setupReveal();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
})();
